"use client";

import Image from "next/image";
import { useEffect, useState } from "react";

import { THANK_YOU_SOURCE } from "@/constants/leadConstants";
import { cn } from "@/lib/utils";

// Same CU artwork the refurl / influencer pages use for their inline thank-you
// step, so the standalone page matches without the editor having to re-upload.
const DEFAULT_LOGO_IMAGE = "/images/influencer_logo.webp";
const DEFAULT_DESKTOP_IMAGE = "/images/influencer_bg_desktop.webp";
const DEFAULT_MOBILE_IMAGE = "/images/influencer_thanks_bg_mobile.webp";

/**
 * Copy baked into ReferralForm1008 / InfluencerForm1007's thank-you state. It is
 * the fallback, not a CMS default: leaving the description fields empty in the
 * admin keeps this wording, filling them overrides it per lead source.
 */
const DEFAULT_DESCRIPTION =
  "<h2>Thank You for Applying</h2><p>Your details have been received and one of our counsellors will reach out to you</p>";

interface ThankYou1009Data {
  logo_image?: string;
  logo_alt?: string;
  desktop_bg_image?: string;
  mobile_bg_image?: string;
  alt_image?: string;
  description?: string;
  referral_description?: string;
  influencer_description?: string;
}

interface ThankYou1009Props {
  data?: { data?: ThankYou1009Data } | ThankYou1009Data;
}

export default function ThankYou1009({ data }: ThankYou1009Props) {
  const thankYouData = ((data as { data?: ThankYou1009Data })?.data ??
    data) as ThankYou1009Data | undefined;

  // Which form sent the visitor here — ReferralForm1 / InfluencerForm1 stamp this
  // before routing to /thank-you. Read after mount; sessionStorage is client-only.
  const [source, setSource] = useState<string | null>(null);

  useEffect(() => {
    setSource(sessionStorage.getItem(THANK_YOU_SOURCE.STORAGE_KEY));
  }, []);

  const sourceDescription =
    source === THANK_YOU_SOURCE.REFERRAL
      ? thankYouData?.referral_description
      : source === THANK_YOU_SOURCE.INFLUENCER
        ? thankYouData?.influencer_description
        : "";

  const description =
    sourceDescription || thankYouData?.description || DEFAULT_DESCRIPTION;

  const logoImage = thankYouData?.logo_image || DEFAULT_LOGO_IMAGE;
  const logoAlt = thankYouData?.logo_alt || "Logo";
  const altImage = thankYouData?.alt_image || "Thank you background";
  const desktopImage = thankYouData?.desktop_bg_image || DEFAULT_DESKTOP_IMAGE;
  const mobileImage = thankYouData?.mobile_bg_image || DEFAULT_MOBILE_IMAGE;

  // Geometry mirrors the `isVerified` branch of ReferralForm1008 / InfluencerForm1007
  // exactly, including the 68px the logo header takes off the mobile viewport.
  return (
    <div className="w-full bg-[#FFFFFF] font-['Poppins']">
      <div className="mx-auto max-w-[1200px] px-4 pb-4 min-[768px]:px-[42px] min-[768px]:pb-[42px]">
        <header className="w-full p-4 min-[768px]:px-[42px] min-[768px]:mb-[10px]">
          {logoImage && (
            <Image
              src={logoImage}
              alt={logoAlt}
              width={120}
              height={42}
              className="h-6 min-[768px]:h-[30px] my-[6px] min-[768px]:my-0 w-28 min-[768px]:w-33 object-contain object-left"
              priority
            />
          )}
        </header>
        <div className="w-full relative h-[calc(100vh-68px)] min-[768px]:h-[536px]">
          {desktopImage && (
            <Image
              src={desktopImage}
              alt={altImage}
              fill
              className="hidden min-[768px]:block object-cover"
              priority
            />
          )}
          {mobileImage && (
            <Image
              src={mobileImage}
              alt={altImage}
              fill
              className="block min-[768px]:hidden object-cover"
              priority
            />
          )}
          <div className="max-w-[700px] absolute top-[calc(50%-68px)] min-[768px]:top-1/2 -translate-y-1/2 mx-[18px] min-[768px]:ml-[42px]">
            <div
              className={cn(
                "text-[#FFFFFF] text-center min-[768px]:text-left",
                "[&_h2]:text-[30px] min-[768px]:[&_h2]:text-[32px] [&_h2]:font-bold [&_h2]:leading-[138%] min-[768px]:[&_h2]:mb-[18px]",
                "[&_p]:text-xs min-[768px]:[&_p]:text-[16px] [&_p]:font-normal [&_p]:leading-[22px]",
              )}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
